'use client';

import { useNow } from '../../lib/useNow';
import { Target } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { formatDuration, parseTs } from '../../lib/format';
import type { BrainGoal } from '../../lib/types';
import type { GoalStatusSnapshot } from '../../../src/shared/wireContract';

/** Guarded browser mirror of chatEvents.goalStatusSnapshot. Runtime backend imports cannot cross the
 * web build root; tests/contract/webMirrorContracts.test.ts pins this body to the daemon projection. */
export function goalStatusSnapshot(goal: Pick<BrainGoal, 'status' | 'goal' | 'subgoals' | 'turns_used' | 'turn_budget' | 'paused_reason' | 'last_evidence'> | null): GoalStatusSnapshot {
  if (!goal) return null;
  let subgoals: { done: number; total: number } | undefined;
  const list: unknown = JSON.parse(goal.subgoals);
  if (Array.isArray(list) && list.length) {
    subgoals = { done: list.filter((item: unknown) => typeof item === 'object' && item !== null && (item as { done?: unknown }).done === true).length, total: list.length };
  }
  return {
    status: goal.status, goal: goal.goal, turnsUsed: goal.turns_used, turnBudget: goal.turn_budget,
    ...(subgoals ? { subgoals } : {}),
    ...(goal.paused_reason ? { pausedReason: goal.paused_reason } : {}),
    ...(goal.last_evidence ? { evidence: goal.last_evidence } : {}),
  };
}

export function useGoalElapsed(goal: BrainGoal | null): number {
  const startedAt = parseTs(goal?.created_at) ?? Date.now();
  const now = useNow(1_000, goal !== null);
  return goal ? Math.max(0, now - startedAt) : 0;
}

/** The CLI-style active-goal chip for chat surfaces that do not currently have a visible telemetry rail. */
export function GoalStatusInline({ goal }: { goal: BrainGoal }) {
  const { t } = useTranslation();
  const elapsed = useGoalElapsed(goal);
  const turns = goal.turn_budget > 0 ? `${goal.turns_used}/${goal.turn_budget}` : String(goal.turns_used);
  const facts = goalStatusSnapshot(goal);
  const detail = facts?.evidence ? `${goal.goal} — ${facts.evidence}` : goal.goal;
  return (
    <div
      data-testid="chat-goal-status"
      className="flex min-w-0 items-center gap-1.5 text-primary"
      aria-label={`${t.telemetry.goal}: ${turns}, ${formatDuration(elapsed)}, ${detail}`}
    >
      <Target size={11} className="shrink-0" aria-hidden />
      <span className="shrink-0 text-emphasis">{t.telemetry.goal}</span>
      <span className="shrink-0 tabular-nums text-muted-foreground">{turns} · {formatDuration(elapsed)}</span>
      <span className="text-muted-foreground" aria-hidden>·</span>
      <span className="min-w-0 truncate text-foreground" title={detail}>{goal.goal}</span>
    </div>
  );
}
